import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { storeReferralCode, generateAppStoreLinks } from '@/utils/referralUtils';
interface MobileReferralViewProps {
  referralCode?: string | null;
  isProcessing: boolean;
  visitorId?: string;
  campaign?: string | null;
}
const MobileReferralView: React.FC<MobileReferralViewProps> = ({
  referralCode,
  isProcessing,
  campaign
}) => {
  const [appLinks, setAppLinks] = useState({
    appleAppStoreUrl: '',
    googlePlayStoreUrl: ''
  });
  useEffect(() => {
    // Store the referral code when component mounts
    if (referralCode) {
      storeReferralCode(referralCode, campaign || undefined);
    }

    // Generate app store links with tracking parameters
    const links = generateAppStoreLinks(referralCode);
    setAppLinks(links);
  }, [referralCode, campaign]);
  return <div className="flex flex-col min-h-screen bg-gradient-to-b from-purple-600 to-indigo-800">
      <div className="flex-1 flex flex-col items-center justify-center px-4 py-12 text-center">
        {/* Logo */}
        <motion.div className="mb-12" initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }}>
          <img src="/images/5329cbae-0ae5-46ae-8de9-62f4c153dad8.png" alt="The Mat Monster Logo" className="w-64 h-64 object-contain" />
          
        </motion.div>

        {isProcessing ? <div className="flex flex-col items-center justify-center py-4">
            <div className="w-12 h-12 border-4 border-t-purple-500 border-r-transparent border-b-purple-500 border-l-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-white/90">Processing referral...</p>
          </div> : <>
            {/* Invitation Message */}
            <motion.h1 className="text-3xl font-bold text-white mb-6" initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }}>
              You've been invited!
            </motion.h1>

            {/* Discount Card */}
            <motion.div className="bg-white rounded-xl p-6 w-full max-w-md mb-6" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }}>
              <h3 className="text-3xl font-bold text-purple-600 mb-2">30% OFF</h3>
              <div className="w-12 h-1 bg-purple-300 mx-auto mb-2"></div>
              <p className="text-gray-700">on your first 3 months</p>
              <div className="mt-3 bg-purple-500 text-white text-sm py-1 px-4 rounded-full inline-block">
                Special Offer
              </div>
            </motion.div>

            {/* App Description */}
            <motion.p className="text-white text-center mb-6 max-w-md" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.5,
          delay: 0.3
        }}>
              Start your journey with The Mat Monster—track progress, sharpen your game, and unlock premium training content.
            </motion.p>

            {/* App Store Buttons - Side by side */}
            <motion.div className="flex flex-row gap-4 justify-center w-full px-4 mb-4" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.4
        }}>
              <a href={appLinks.appleAppStoreUrl} className="flex items-center justify-center bg-black text-white py-3 px-4 rounded-xl font-medium min-w-32" target="_blank" rel="noopener noreferrer">
                {/* Official Apple App Store icon */}
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 384 512" fill="currentColor" className="mr-2">
                  <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
                </svg>
                App Store
              </a>
              
              <a href={appLinks.googlePlayStoreUrl} className="flex items-center justify-center bg-purple-500 text-white py-3 px-4 rounded-xl font-medium min-w-32" target="_blank" rel="noopener noreferrer">
                {/* Play Store icon */}
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 512 512" className="mr-2" fill="currentColor">
                  <path d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1z" />
                  <path d="M47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0z" />
                  <path d="M104.6 499l280.8-161.2-60.1-60.1L104.6 499z" />
                  <path d="M473.2 225.6l-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8z" />
                </svg>
                Play Store
              </a>
            </motion.div>

            {/* Continue in browser link - with permanent underline */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.5,
          delay: 0.5
        }} className="mb-12">
              <a href="/auth/signup" className="text-white underline">
                Continue in browser instead
              </a>
            </motion.div>

            {/* Referral code confirmation */}
            <motion.div className="text-white/70 text-sm mt-auto text-center max-w-md" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.8,
          delay: 0.6
        }}>
              Your referral code has been saved automatically.
              <br />You'll get 30% off when you sign up in the app.
            </motion.div>
          </>}
      </div>
      
      {/* Footer */}
      <div className="p-4 text-center text-white/50 text-sm">
        <p>© {new Date().getFullYear()} The Mat Monster. All rights reserved.</p>
      </div>
    </div>;
};
export default MobileReferralView;